import { useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { MetricCard } from '../components/MetricCard.js';
import type { MetricStatus } from '../components/MetricSparkline.js';
import { SCORECARD_DISPLAY_NAME, SCORECARD_METRIC_HELP, metricFormat } from '../lib/scorecard-format.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';
import { SPARKLINE_KIND, projectSeries, type DailyMetricsPoint } from './velocity-scorecard.projection.js';

// Empty-string sentinel mirrors weekly-digest.tsx — "all orgs" is the default
// landing so the user sees the portfolio view before drilling into a single
// org. Once an org is picked from the dropdown the URL pins it via ?org=.
const ALL_ORGS = '';

function isValidOrg(v: string | null, orgs: string[]): v is string {
  if (v === null) return false;
  if (v === ALL_ORGS) return true;
  return orgs.includes(v);
}

interface ScorecardMetric {
  metric: string;
  baseline: number;
  current_30d: number;
  delta: number;
  color: MetricStatus;
}

interface IncidentBotApprovedLinkDetail {
  incident_id: string;
  incident_title: string;
  source: 'confluence-postmortem' | 'pagerduty-statuspage';
  source_url: string;
  pr_org: string;
  pr_repo: string;
  pr_number: number;
  confidence: number;
  reason: string;
}

interface TokenDailyPoint {
  date: string;
  tokens_in_total: number;
  // Optional for back-compat with API builds that pre-date the instrumented
  // denominator (the card only reads cache_read_share, which both emit).
  tokens_in_instrumented?: number;
  input_tokens_excl_cache: number;
  cache_read_tokens: number;
  cache_read_share: number;
}

interface ScorecardResponse {
  // 'all' when no org is selected, otherwise the picked org's login.
  org: string;
  metrics: ScorecardMetric[];
  daily: DailyMetricsPoint[];
  // Optional for back-compat with API builds that pre-date the token-usage
  // card — older deploys omit these and the card renders an empty state.
  token_daily?: TokenDailyPoint[];
  cache_read_share_30d?: number | null;
  tokens_in_per_day_baseline?: number | null;
  incidents_linked_to_bot_approved_30d: number;
  incidents_linked_to_bot_approved_30d_detail: IncidentBotApprovedLinkDetail[];
}

// Re-use the label map from the shared lib so tests can import it without
// pulling in the browser-env dependencies from this route module.
const DISPLAY_NAME = SCORECARD_DISPLAY_NAME;

// Picker styles match weekly-digest.tsx so the chrome is identical across
// pages. The custom chevron is rendered as a sibling element because
// background-image SVG chevrons are too faint at 12px on macOS.
const inputStyle: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)',
  height: '36px',
  boxSizing: 'border-box',
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  cursor: 'pointer',
  paddingRight: '32px',
};

const selectWrapStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
};

const selectChevronStyle: CSSProperties = {
  position: 'absolute',
  right: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  color: 'var(--loop-neutral-900)',
  display: 'flex',
  alignItems: 'center',
};

export function VelocityScorecard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState(false);

  const enterpriseOrgs = useApiQuery<{ orgs: Array<{ login: string; opted_in: boolean }> }>(
    ['enterprise-orgs'],
    '/api/enterprise-orgs'
  );
  // Filter to opted-in orgs only — picking a non-opted-in org reliably 403s at
  // /api/scorecard (fetchAllowedOrgs gates on installed ∩ opted-in). Matches
  // cost.tsx's pattern so the dropdown experiences are consistent.
  const orgOptions: string[] = (enterpriseOrgs.data?.orgs ?? [])
    .filter((o) => o.opted_in)
    .map((o) => o.login)
    .sort();

  const rawOrg = searchParams.get('org');
  const org: string = isValidOrg(rawOrg, orgOptions) ? rawOrg : ALL_ORGS;
  const isAllOrgs = org === ALL_ORGS;

  function setOrg(newOrg: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('org', newOrg);
      return next;
    });
  }

  // Omit ?org= for all-orgs to keep the network-tab URL clean; the API treats
  // missing/empty org as all-orgs aggregation.
  const scorecardUrl = isAllOrgs
    ? '/api/scorecard'
    : `/api/scorecard?org=${encodeURIComponent(org)}`;
  const { data, isLoading, error } = useApiQuery<ScorecardResponse>(
    ['scorecard', org],
    scorecardUrl
  );

  // Council tokens/day: average daily total over the window vs the pinned
  // pre-rollout baseline. Lower is better (caching cuts tokens), so a drop
  // below baseline is green.
  const tokenPts = data?.token_daily ?? [];
  const tokensAvgPerDay = tokenPts.length
    ? Math.round(tokenPts.reduce((sum, p) => sum + p.tokens_in_total, 0) / tokenPts.length)
    : null;
  const tokensBaseline = data?.tokens_in_per_day_baseline ?? null;
  const tokensDelta = tokensAvgPerDay != null && tokensBaseline != null ? tokensAvgPerDay - tokensBaseline : null;
  const tokensStatus: MetricStatus | undefined =
    tokensDelta == null ? undefined : tokensDelta < 0 ? 'green' : tokensDelta > 0 ? 'red' : undefined;

  // Cache-read share: the pre-rollout baseline is definitionally 0% (caching
  // was off), so any positive share is the win. Null share means the window
  // has no instrumented (SDK-path) traffic yet — render the empty state with
  // no baseline rather than a hollow "0% → 0%".
  const cacheShare = data?.cache_read_share_30d ?? null;
  const cacheShareBaseline = cacheShare != null ? 0 : null;
  const cacheShareDelta = cacheShare != null ? cacheShare : null;
  const cacheShareStatus: MetricStatus | undefined =
    cacheShare != null && cacheShare > 0 ? 'green' : undefined;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>Velocity Scorecard</h2>
        <ConceptHelpIcon slug="velocity-scorecard" />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
        <div>
          <label htmlFor="scorecard-org-select" style={{ display: 'block', marginBottom: 4, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>Org</label>
          <div style={selectWrapStyle}>
            <select
              id="scorecard-org-select"
              value={org}
              onChange={e => setOrg(e.target.value)}
              style={selectStyle}
              disabled={enterpriseOrgs.isLoading}
            >
              <option value={ALL_ORGS}>All orgs</option>
              {orgOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <span style={selectChevronStyle} aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </span>
          </div>
        </div>
      </div>

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error && <p className="error">Error: {String(error)}</p>}

      {data && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 16,
              alignItems: 'stretch',
            }}
          >
            {(data.metrics ?? []).map(m => {
              const sparklineKind = SPARKLINE_KIND[m.metric];
              const sparkline = sparklineKind != null
                ? { data: projectSeries(m.metric, data.daily ?? []), kind: sparklineKind }
                : undefined;
              return (
                <MetricCard
                  key={m.metric}
                  name={DISPLAY_NAME[m.metric] ?? m.metric}
                  baseline={m.baseline}
                  current30d={m.current_30d}
                  delta={m.delta}
                  status={m.color}
                  format={metricFormat(m.metric)}
                  helpText={SCORECARD_METRIC_HELP[m.metric]}
                  sparkline={sparkline}
                />
              );
            })}

            {/* Token-usage cards (prompt-caching observability). token_daily is
                its own response series, not part of `daily`, so the line is
                built inline rather than via projectSeries. The total line is
                backfilled across the full window; cache-read share starts at
                the DV_DISPATCH=sdk rollout (older runs lack the breakdown). */}
            <MetricCard
              key="tokens_in_per_day"
              name={DISPLAY_NAME['tokens_in_per_day'] ?? 'Council tokens / day'}
              // Baseline = avg/day over the pinned pre-rollout week; Last-30d =
              // avg/day over the window. Both per-day averages so they compare
              // directly; a drop below baseline (negative delta) is the win.
              baseline={tokensBaseline}
              current30d={tokensAvgPerDay}
              delta={tokensDelta}
              status={tokensStatus}
              format={metricFormat('tokens_in_per_day')}
              helpText={SCORECARD_METRIC_HELP['tokens_in_per_day']}
              sparkline={{
                data: (data.token_daily ?? []).map(p => ({ date: p.date, value: p.tokens_in_total })),
                kind: 'line',
              }}
            />
            <MetricCard
              key="cache_read_share_30d"
              name={DISPLAY_NAME['cache_read_share_30d'] ?? 'Cache-read share (30d)'}
              baseline={cacheShareBaseline}
              current30d={cacheShare}
              delta={cacheShareDelta}
              status={cacheShareStatus}
              format={metricFormat('cache_read_share_30d')}
              helpText={SCORECARD_METRIC_HELP['cache_read_share_30d']}
              sparkline={{
                data: (data.token_daily ?? []).map(p => ({ date: p.date, value: p.cache_read_share })),
                kind: 'line',
              }}
            />

            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="card"
              style={{
                marginBottom: 0,
                textAlign: 'left',
                cursor: 'pointer',
                background: 'var(--loop-neutral-0)',
                font: 'inherit',
                color: 'inherit',
                display: 'flex',
                flexDirection: 'column',
              }}
              aria-expanded={expanded}
              aria-controls="incidents-bot-approved-detail"
            >
              <div style={{ fontSize: '0.875rem', fontWeight: 528, color: 'var(--loop-neutral-900)', marginBottom: 'var(--loop-space-100)' }}>
                Incidents linked to bot-approved PRs (30d)
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)' }}>Count</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>
                    {data.incidents_linked_to_bot_approved_30d}
                  </div>
                </div>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '0.75rem',
                    color: 'var(--loop-blue-700)',
                  }}
                >
                  {expanded ? 'Hide details ▴' : 'Show details ▾'}
                </span>
              </div>
            </button>
          </div>

          {expanded && (
            <div
              id="incidents-bot-approved-detail"
              className="card"
              style={{ marginTop: 16 }}
            >
              {data.incidents_linked_to_bot_approved_30d_detail.length === 0 ? (
                <p style={{ color: 'var(--loop-neutral-600)', fontSize: '0.875rem', margin: 0 }}>
                  No (incident, bot-approved PR) pairs in the last 30 days.
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--loop-neutral-700)' }}>
                      <th style={{ padding: '4px 8px' }}>Incident</th>
                      {/* Org column surfaces in all-orgs view so the reader
                          can disambiguate two PRs with the same number from
                          different orgs. Hidden in per-org view to save width. */}
                      {isAllOrgs && <th style={{ padding: '4px 8px' }}>Org</th>}
                      <th style={{ padding: '4px 8px' }}>PR</th>
                      <th style={{ padding: '4px 8px' }}>Confidence</th>
                      <th style={{ padding: '4px 8px' }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.incidents_linked_to_bot_approved_30d_detail.map((row, idx) => (
                      <tr
                        key={`${row.incident_id}#${row.pr_org}/${row.pr_repo}#${row.pr_number}#${idx}`}
                        style={{ borderTop: '1px solid var(--loop-neutral-200)' }}
                      >
                        <td style={{ padding: '6px 8px' }}>
                          <a
                            href={row.source_url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: 'var(--loop-blue-700)' }}
                          >
                            {row.incident_title || row.incident_id}
                          </a>
                          <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)' }}>
                            {row.source}
                          </div>
                        </td>
                        {isAllOrgs && (
                          <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {row.pr_org}
                          </td>
                        )}
                        <td style={{ padding: '6px 8px' }}>
                          <Link
                            to={`/repos/${row.pr_org}/${row.pr_repo}/pr/${row.pr_number}`}
                            style={{ color: 'var(--loop-blue-700)' }}
                          >
                            {row.pr_org}/{row.pr_repo}#{row.pr_number}
                          </Link>
                        </td>
                        <td style={{ padding: '6px 8px' }}>{row.confidence}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--loop-neutral-700)' }}>{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

        </>
      )}
    </div>
  );
}
